import React from "react";
var __jsx = React.createElement;

/* eslint-disable max-lines */
import { CircularProgress } from '@material-ui/core';
import { Editor, EditorState } from 'draft-js';
import { useEffect, useRef, useState } from 'react';
import extendedBlockRenderMap from './BlockRenderOverrides';
import compositeDecoratorGenerator from './CompositeDecorator/compositeDecoratorGenerator';
import DebugButtons from './DebugButtons';
import { Container, EditorContainer, Loader } from './RichTextEditor.style';
import { ToggleBlockEditToolbar } from './ToggleBlockEditToolbar';
import { useBlockHelpers as useBlockHelpersInsideEditor, useHandleKeyBindingFnCallback, useHandleKeyCommandCallback, useHandlePastedText, useHandleReturnCallback } from './hooks';
import { useBlockRendererCallback, useInsertTableBlockCallback, useMoveBlockUpOrDownCallback, useRemoveBlockCallback, useRemoveEntityCallback, useUpdateBlockDataCallback, useUpdateEntityCallback } from './hooks/block';
import { useAddAffiliationLinkCallback, useAddExternalLinkCallback, useAddInternalLinkCallback, useHandleInlineStyleToggleCallback, useHighlightToggleCallback, useResetStyleToggleCallback } from './hooks/inline';
import InlineToolbar from './tools/InlineToolbar/InlineToolbar';
import { ToggleBlockToolbar } from './tools/ToggleBlockToolbar';
import { EntityEditingController } from './tools/entity-editing/EntityEditingController';
import { transformDraftjsToGenericState } from './transformer';
import { transformGenericToDraftjsState } from './transformer/transformGenericToDraftjs';
import { RichTextEditorMethodsProvider } from './types';
import { styleMap } from './util/style';

var RichTextEditor = function RichTextEditor(_ref) {
  var inputId = _ref.inputId,
      initialRawState = _ref.initialRawState,
      onRawStateChange = _ref.onRawStateChange,
      initialEditorState = _ref.initialEditorState,
      enabledBlocks = _ref.enabledBlocks;

  var _useState = useState(function () {
    return initialEditorState !== null && initialEditorState !== void 0 ? initialEditorState : initialRawState ? EditorState.createWithContent(transformGenericToDraftjsState(initialRawState)) : EditorState.createEmpty();
  }),
      editorState = _useState[0],
      setEditorState = _useState[1];

  var editorRef = useRef(null);

  var _useState2 = useState(false),
      loading = _useState2[0],
      setLoading = _useState2[1];

  var handleReturn = useHandleReturnCallback(editorState, setEditorState);
  var handleKeyCommand = useHandleKeyCommandCallback(setEditorState);
  var handleKeyBindingFn = useHandleKeyBindingFnCallback(editorState, setEditorState);
  var handleHighlightToggle = useHighlightToggleCallback(setEditorState);
  var handleInlineStyleToggle = useHandleInlineStyleToggleCallback(setEditorState);
  var handleResetStyleToggle = useResetStyleToggleCallback(setEditorState);
  var insertTableBlock = useInsertTableBlockCallback(setEditorState);
  var removeBlockHelper = useRemoveBlockCallback(setEditorState);
  var removeEntity = useRemoveEntityCallback(setEditorState);
  var moveBlockUpOrDown = useMoveBlockUpOrDownCallback(setEditorState);
  var updateEntity = useUpdateEntityCallback(setEditorState);
  var updateBlockDataHelper = useUpdateBlockDataCallback(setEditorState); // To update/remove blocks from the inside of the Editor, dedicated helpers are needed

  var _useBlockHelpersInsid = useBlockHelpersInsideEditor(removeBlockHelper, updateBlockDataHelper),
      removeBlockInsideEditor = _useBlockHelpersInsid.removeBlock,
      updateBlockDataInsideEditor = _useBlockHelpersInsid.updateBlockData;

  var blockRenderer = useBlockRendererCallback({
    editor: editorRef.current,
    editorState: editorState
  });
  var addExternalLink = useAddExternalLinkCallback(setEditorState);
  var addInternalLink = useAddInternalLinkCallback(setEditorState);
  var addAffiliationLink = useAddAffiliationLinkCallback(setEditorState);
  var handlePastedText = useHandlePastedText(setEditorState, setLoading); // Previously Debounce

  useEffect(function () {
    onRawStateChange(transformDraftjsToGenericState(editorState.getCurrentContent()));
  },
  /**
   * `onRawStateChange` is not specified in the dependencies since it will make an infinite render loop:
   * - Calling `onRawStateChange` with a new value will cause a re-render
   * - On re-render, `onRawStateChange` has a new value
   * - This `useEffect` will be called again
   * - By only specifying `editorState`, we make sure this variable is the sole trigger of the effect and
   * we make sure that the `onRawStateChange` called inside will always be the latest
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [editorState]);
  useEffect(function () {
    setEditorState(function (currentEditorState) {
      return EditorState.set(currentEditorState, {
        decorator: compositeDecoratorGenerator({
          setEditorState: setEditorState
        })
      });
    });
  }, []);
  var isDebugMode = process.env.NODE_ENV === 'development';
  return __jsx(React.Fragment, null, __jsx(Container, null, __jsx(EntityEditingController, {
    editorRef: editorRef,
    editorState: editorState,
    removeBlock: removeBlockHelper,
    removeEntity: removeEntity,
    updateBlockData: updateBlockDataHelper,
    updateEntity: updateEntity
  }), __jsx(InlineToolbar, {
    editorRef: editorRef,
    editorState: editorState,
    setEditorState: setEditorState,
    handleInlineStyleToggle: handleInlineStyleToggle,
    handleResetStyleToggle: handleResetStyleToggle,
    handleHighlightToggle: handleHighlightToggle,
    addExternalLink: addExternalLink,
    addInternalLink: addInternalLink,
    addAffiliationLink: addAffiliationLink,
    enabledBlocks: enabledBlocks
  }), __jsx(ToggleBlockToolbar, {
    editorState: editorState,
    setEditorState: setEditorState,
    enabledBlocks: enabledBlocks,
    insertTable: insertTableBlock
  }), __jsx(ToggleBlockEditToolbar, {
    editorState: editorState,
    updateBlockData: updateBlockDataHelper,
    removeBlock: removeBlockHelper,
    moveBlockUpOrDown: moveBlockUpOrDown
  }), __jsx(EditorContainer, {
    onClick: function onClick() {
      var _editorRef$current;

      return (_editorRef$current = editorRef.current) === null || _editorRef$current === void 0 ? void 0 : _editorRef$current.focus();
    }
  }, __jsx(RichTextEditorMethodsProvider, {
    removeBlock: removeBlockInsideEditor,
    updateBlockData: updateBlockDataInsideEditor
  }, __jsx(Editor, {
    ariaLabelledBy: inputId,
    ref: editorRef,
    editorState: editorState,
    customStyleMap: styleMap,
    onChange: setEditorState,
    editorKey: "foobar",
    handleKeyCommand: handleKeyCommand,
    keyBindingFn: handleKeyBindingFn,
    handleReturn: handleReturn,
    blockRenderMap: extendedBlockRenderMap,
    blockRendererFn: blockRenderer,
    handlePastedText: handlePastedText
  })), loading && __jsx(Loader, null, __jsx(CircularProgress, null)))), isDebugMode && __jsx(DebugButtons, {
    editorState: editorState
  }));
};

export default RichTextEditor;